import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { PermissionsProvider, RolesProvider, UsersProvider } from '../../contexts';
import { SidebarLayout } from '../../layouts';
import { PermissionsGuard } from './Components';
import { Loader } from './loader.routes';

const UsersList = Loader(lazy(() => import('../../content/pages/Users/UsersList')));
const RolesList = Loader(lazy(() => import('../pages/Roles')));
const PermissionsList = Loader(lazy(() => import('../pages/Permissions')));
const UserDetails = Loader(lazy(() => import('../../content/pages/Users/UserDetails')));
const UserUpdate = Loader(lazy(() => import('../../content/pages/Users/UserUpdate')));
const CreateUser = Loader(lazy(() => import('../../content/pages/Users/UserCreate')));
const UserBulkCreate = Loader(lazy(() => import('../pages/Users/UserBulk/UserBulkCreate')));
const CreateSkinMonitoring = Loader(lazy(() => import('../../content/pages/Users/SkinMonitoringCreate')));

type CustomRouteObject = RouteObject & {
  children?: CustomRouteObject[];
};

export const createUsersRoutes = (): CustomRouteObject => ({
  path: 'users',
  element: <SidebarLayout />,
  children: [
    {
      path: '',
      element: (
        <PermissionsGuard allowedPermissions={['viewAllUsers', 'viewClientUsers', 'viewVenueUsers']} should404>
          <Navigate to="/users/list" replace />
        </PermissionsGuard>
      )
    },
    {
      path: 'list',
      element: (
        <PermissionsGuard allowedPermissions={['viewAllUsers', 'viewClientUsers', 'viewVenueUsers']} should404>
          <UsersProvider>
            <UsersList />
          </UsersProvider>
        </PermissionsGuard>
      )
    },
    {
      path: 'roles',
      element: (
        <PermissionsGuard allowedPermissions={['viewUserRole']} should404>
          <RolesProvider>
            <RolesList />
          </RolesProvider>
        </PermissionsGuard>
      )
    },
    {
      path: 'permissions',
      element: (
        <PermissionsGuard allowedPermissions={['viewUserPermission']} should404>
          <PermissionsProvider>
            <PermissionsList />
          </PermissionsProvider>
        </PermissionsGuard>
      )
    },
    {
      path: `:userId/`,
      element: (
        <PermissionsGuard allowedPermissions={['viewAvaniUser']} should404 ownUserId>
          <UsersProvider>
            <UserDetails />
          </UsersProvider>
        </PermissionsGuard>
      )
    },
    {
      path: `:userId/update`,
      element: (
        <PermissionsGuard allowedPermissions={['editAvaniUser']} should404 ownUserId>
          <UsersProvider>
            <UserUpdate />
          </UsersProvider>
        </PermissionsGuard>
      )
    },
    {
      path: 'create',
      element: (
        <PermissionsGuard allowedPermissions={['createAvaniUser']} should404>
          <UsersProvider>
            <CreateUser />
          </UsersProvider>
        </PermissionsGuard>
      )
    },
    {
      path: 'bulk-create',
      element: (
        <PermissionsGuard allowedPermissions={['createAvaniUser']} should404>
          <UsersProvider>
            <UserBulkCreate />
          </UsersProvider>
        </PermissionsGuard>
      )
    },
    {
      path: 'skin-monitoring',
      element: (
        <PermissionsGuard allowedPermissions={['editAvaniUser']} should404>
          <CreateSkinMonitoring />
        </PermissionsGuard>
      )
    }
  ]
});
